import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import { sentryContextService } from './services/sentryContext';
import { apiService } from './services/api';
import { Login } from './components/Login';
import { Dashboard } from './components/Dashboard';

interface User {
  id: string;
  email: string;
  name?: string;
  /** Admin role from login response — drives sidebar/route access (super_admin | admin | legacy roles). */
  role?: string;
  isAuthenticated: boolean;
  twoFactorEnabled?: boolean;
  lastLogin?: string;
  createdAt?: string;
}

// Component to track route changes in Sentry
const RouteTracker: React.FC<{ user: User | null }> = ({ user }) => {
  const location = useLocation();

  useEffect(() => {
    // Set session context with current route
    sentryContextService.setSessionContext({
      route: location.pathname,
      referrer: document.referrer,
    });

    // Add breadcrumb for route navigation
    sentryContextService.addBreadcrumb(
      `Admin navigated to ${location.pathname}`,
      'navigation',
      'info',
      { pathname: location.pathname, search: location.search }
    );
  }, [location]);

  useEffect(() => {
    // Set user context when user changes
    if (user && user.isAuthenticated) {
      sentryContextService.setUserContext(user);
    } else {
      sentryContextService.clearUserContext();
    }
  }, [user]);

  return null;
};

const AppContent: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const handleLogin = (userData: User) => {
    setUser(userData);
    localStorage.setItem('admin_user', JSON.stringify(userData));
    navigate('/');
  };

  const handleLogout = useCallback(() => {
    // Refresh-Token serverseitig revoken + httpOnly-Cookie löschen (best-effort, blockt nicht).
    void apiService.logoutAdmin();
    setUser(null);
    localStorage.removeItem('admin_user');
    apiService.clearToken();
    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    const savedUser = localStorage.getItem('admin_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setIsLoading(false);
  }, []);

  // Set up authentication handlers for API service
  useEffect(() => {
    const handleTokenExpired = () => {
      // Token expired - show notification and redirect to login
      toast.error('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.');
      handleLogout();
    };

    const handleAuthFailure = () => {
      // Authentication failed - redirect to login
      toast.error('Authentifizierung fehlgeschlagen. Bitte melden Sie sich erneut an.');
      handleLogout();
    };

    // Register handlers with API service
    apiService.setAuthHandlers(handleTokenExpired, handleAuthFailure);

    // Cleanup on unmount
    return () => {
      apiService.setAuthHandlers(undefined, undefined);
    };
  }, [handleLogout]);

  const handleUserUpdate = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('admin_user', JSON.stringify(updatedUser));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <RouteTracker user={user} />
      <Routes>
        <Route
          path="/login"
          element={
            user?.isAuthenticated ?
              <Navigate to="/" replace /> :
              <Login onLogin={handleLogin} />
          }
        />
        <Route
          path="/*"
          element={
            user?.isAuthenticated ?
              <Dashboard user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} /> :
              <Navigate to="/login" replace />
          }
        />
      </Routes>
    </>
  );
};

const AppRouter: React.FC = () => {
  return (
    <ErrorBoundary showHomeButton>
      <Router>
        <AppContent />
        <Toaster />
      </Router>
    </ErrorBoundary>
  );
};

export default AppRouter;